import { ref, onMounted, onBeforeUnmount } from "vue";
import { useTheme } from "vuetify";
import { Highcharts } from "@/plugins/highcharts";

export function useHighchartsTheme() {
  const theme = useTheme();
  const isDark = ref(theme.global.current.value.dark);

  const getHighchartsTheme = (dark) => {
    if (dark) {
      return {
        colors: [
          "#7CB5EC",
          "#FFA500",
          "#90EE7E",
          "#F45B5B",
          "#E4D354",
          "#2B908F",
          "#F15C80",
          "#E4D354",
        ],
        chart: {
          backgroundColor: "#1E1E1E",
          style: {
            fontFamily: "IBM Plex Sans",
            color: "#E0E0E0",
          },
          plotBorderColor: "#606063",
        },
        title: {
          style: {
            color: "#E0E0E0",
            fontSize: "16px",
          },
        },
        subtitle: {
          style: {
            color: "#A0A0A0",
          },
        },
        xAxis: {
          gridLineColor: "#404043",
          labels: {
            style: {
              color: "#E0E0E0",
            },
          },
          lineColor: "#404043",
          minorGridLineColor: "#303033",
          tickColor: "#404043",
          title: {
            style: {
              color: "#A0A0A0",
            },
          },
        },
        yAxis: {
          gridLineColor: "#404043",
          labels: {
            style: {
              color: "#E0E0E0",
            },
          },
          lineColor: "#404043",
          minorGridLineColor: "#303033",
          tickColor: "#404043",
          title: {
            style: {
              color: "#A0A0A0",
            },
          },
        },
        tooltip: {
          backgroundColor: "#424242",
          style: {
            color: "#F0F0F0",
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              color: "#E0E0E0",
            },
            marker: {
              lineColor: "#333",
            },
          },
        },
        legend: {
          backgroundColor: "transparent",
          itemStyle: {
            color: "#E0E0E0",
          },
          itemHoverStyle: {
            color: "#FFF",
          },
          itemHiddenStyle: {
            color: "#606063",
          },
          title: {
            style: {
              color: "#E0E0E0",
            },
          },
        },
        credits: {
          style: {
            color: "#666",
          },
        },
        drilldown: {
          activeAxisLabelStyle: {
            color: "#F0F0F3",
          },
          activeDataLabelStyle: {
            color: "#F0F0F3",
          },
        },
        navigation: {
          buttonOptions: {
            symbolStroke: "#DDDDDD",
            theme: {
              fill: "#505053",
            },
          },
        },
        rangeSelector: {
          buttonTheme: {
            fill: "#505053",
            stroke: "#000000",
            style: {
              color: "#E0E0E0",
            },
            states: {
              hover: {
                fill: "#707073",
                stroke: "#000000",
                style: {
                  color: "#FFFFFF",
                },
              },
              select: {
                fill: "#7CB5EC",
                stroke: "#000000",
                style: {
                  color: "#FFFFFF",
                },
              },
            },
          },
          inputBoxBorderColor: "#505053",
          inputStyle: {
            backgroundColor: "#333",
            color: "#E0E0E0",
          },
          labelStyle: {
            color: "#E0E0E0",
          },
        },
        navigator: {
          handles: {
            backgroundColor: "#666",
            borderColor: "#AAA",
          },
          outlineColor: "#CCC",
          maskFill: "rgba(180,180,255,0.2)",
          series: {
            color: "#7798BF",
            lineColor: "#A6C7ED",
          },
          xAxis: {
            gridLineColor: "#505053",
          },
        },
        scrollbar: {
          barBackgroundColor: "#808083",
          barBorderColor: "#808083",
          buttonArrowColor: "#CCC",
          buttonBackgroundColor: "#606063",
          buttonBorderColor: "#606063",
          rifleColor: "#FFF",
          trackBackgroundColor: "#404043",
          trackBorderColor: "#404043",
        },
      };
    } else {
      return {
        colors: [
          "#7CB5EC",
          "#FFA500",
          "#90ED7D",
          "#F7A35C",
          "#8085E9",
          "#F15C80",
          "#E4D354",
          "#2B908F",
        ],
        chart: {
          backgroundColor: "#FFFFFF",
          style: {
            fontFamily: "IBM Plex Sans",
            color: "#333333",
          },
        },
        title: {
          style: {
            color: "#333333",
            fontSize: "16px",
          },
        },
        xAxis: {
          gridLineColor: "#E6E6E6",
          labels: {
            style: {
              color: "#333333",
            },
          },
          lineColor: "#C0D0E0",
          title: {
            style: {
              color: "#666666",
            },
          },
        },
        yAxis: {
          gridLineColor: "#E6E6E6",
          labels: {
            style: {
              color: "#333333",
            },
          },
          lineColor: "#C0D0E0",
          title: {
            style: {
              color: "#666666",
            },
          },
        },
        tooltip: {
          backgroundColor: "#FFFFFF",
          style: {
            color: "#333333",
          },
        },
        legend: {
          itemStyle: {
            color: "#333333",
          },
          itemHoverStyle: {
            color: "#000000",
          },
        },
        rangeSelector: {
          buttonTheme: {
            fill: "#f8f8f8",
            stroke: "#cccccc",
            style: {
              color: "#333333",
            },
            states: {
              hover: {
                fill: "#e6e6e6",
                stroke: "#cccccc",
                style: {
                  color: "#000000",
                },
              },
              select: {
                fill: "#7CB5EC",
                stroke: "#000000",
                style: {
                  color: "#FFFFFF",
                },
              },
            },
          },
          inputBoxBorderColor: "#cccccc",
          inputStyle: {
            backgroundColor: "#ffffff",
            color: "#333333",
          },
          labelStyle: {
            color: "#666666",
          },
        },
      };
    }
  };

  const applyTheme = (dark) => {
    isDark.value = dark;
    const themeOptions = getHighchartsTheme(dark);
    Highcharts.setOptions(themeOptions);

    // Trigger reflow on all charts
    Highcharts.charts.forEach((chart) => {
      if (chart) {
        chart.reflow();
      }
    });
  };

  const handleThemeChange = (event) => {
    applyTheme(event.detail.isDark);
  };

  onMounted(() => {
    // Apply initial theme
    applyTheme(theme.global.current.value.dark);

    // Listen for theme changes
    window.addEventListener("theme-changed", handleThemeChange);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("theme-changed", handleThemeChange);
  });

  return {
    isDark,
    getHighchartsTheme,
  };
}
